/**
 * Departure Date Sorting and Filtering for Upcoming Trips Collection
 * Handles sorting by departure_date metafield and hiding past trips
 */

// Departure Date Sorting script loaded

class DepartureDateHandler {
  constructor() {
    this.init();
  }

  init() {
    // Check if we're on the upcoming trips template and sort_by is set
    const sortBy = this.getSortByValue();
    if (sortBy === 'by_meta_date') {
      this.processProducts();
    }
  }

  getSortByValue() {
    // Check if sort_by is available from the window variable set by Liquid
    if (window.departureDateSorting && window.departureDateSorting.sortBy) {
      return window.departureDateSorting.sortBy;
    }

    // Fallback: Try to get sort_by from the URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    let sortBy = urlParams.get('sort_by');

    // If not in URL, check if it's set in the template (from Liquid)
    if (!sortBy) {
      // Look for a data attribute or script tag that contains the sort_by value
      const sortByElement = document.querySelector('[data-sort-by]');
      if (sortByElement) {
        sortBy = sortByElement.dataset.sortBy;
      }
    }

    return sortBy;
  }

  getSectionId() {
    // Try to get section ID from window variable
    if (window.departureDateSorting && window.departureDateSorting.sectionId) {
      return window.departureDateSorting.sectionId;
    }

    // Fallback: look for section ID in the DOM
    const sectionElement = document.querySelector('[data-section-id]');
    if (sectionElement) {
      return sectionElement.dataset.sectionId;
    }

    // Another fallback: look for section ID in class names
    const sectionClass = document.querySelector('.section-');
    if (sectionClass) {
      const classList = sectionClass.className;
      const match = classList.match(/section-(\d+)/);
      if (match) {
        return match[1];
      }
    }

    return null;
  }

  processProducts() {
    // Look for product grid in both main collection and featured collection
    let productGrid = document.getElementById('product-grid');

    // If not found, look for featured collection grid
    if (!productGrid) {
      const sectionId = this.getSectionId();

      if (sectionId) {
        productGrid = document.querySelector(
          '#Slider-' +
            sectionId +
            ' .product-grid, #Slider-' +
            sectionId +
            ' .grid'
        );
      }

      // Try alternative selectors
      if (!productGrid) {
        productGrid = document.querySelector('.product-grid, .grid');
      }
    }

    if (!productGrid) {
      return;
    }

    // Find the parent collection element and add sorting class
    const collectionElement = document.querySelector(
      '.product-collection, .collection'
    );

    if (collectionElement) {
      collectionElement.classList.add('departure-sorting');
    }

    // Look for products in both main collection and featured collection structures
    let products = Array.from(productGrid.querySelectorAll('.grid__item'));

    // If no products found in grid__item, try other selectors for featured collection
    if (products.length === 0) {
      products = Array.from(
        productGrid.querySelectorAll('.swiper-slide, [id^="Slide-"]')
      );
    }

    // If no products found, ensure collection is visible and return
    if (products.length === 0) {
      const collectionElement = document.querySelector(
        '.product-collection, .collection'
      );
      if (collectionElement) {
        collectionElement.classList.remove('departure-sorting');
      }
      return;
    }

    // Extract products with their departure dates
    const productsWithDates = products.map(product => {
      const departureDate = this.getDepartureDate(product);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day

      return {
        element: product,
        departureDate: departureDate,
        isPast: departureDate && departureDate < today
      };
    });

    // Filter out past trips
    const upcomingProducts = productsWithDates.filter(item => !item.isPast);

    // Sort by departure date (earliest first)
    upcomingProducts.sort((a, b) => {
      if (!a.departureDate && !b.departureDate) return 0;
      if (!a.departureDate) return 1;
      if (!b.departureDate) return -1;
      return a.departureDate - b.departureDate;
    });

    // Reorder the DOM elements
    this.reorderProducts(
      productGrid,
      upcomingProducts.map(item => item.element)
    );

    // Update product count if available
    this.updateProductCount(upcomingProducts.length, productsWithDates.length);
  }

  getDepartureDate(productElement) {
    // Try to get departure date from various possible locations
    let departureDate = null;

    // Method 1: Look for a data attribute on the product element
    const dateAttr = productElement.dataset.departureDate;

    if (dateAttr) {
      departureDate = new Date(dateAttr);
      if (!isNaN(departureDate.getTime())) {
        return departureDate;
      }
    }

    // Method 2: Look for departure date in product card content
    const dateElement = productElement.querySelector('[data-departure-date]');

    if (dateElement) {
      departureDate = new Date(dateElement.dataset.departureDate);
      if (!isNaN(departureDate.getTime())) {
        return departureDate;
      }
    }

    // Method 3: Look for departure date in product title or description
    const titleElement = productElement.querySelector(
      '.card__heading, .product-title, h3, h4'
    );

    if (titleElement) {
      const text = titleElement.textContent;

      const dateMatch = text.match(
        /(\d{4}-\d{2}-\d{2})|(\d{2}\/\d{2}\/\d{4})|(\d{2}-\d{2}-\d{4})/
      );

      if (dateMatch) {
        departureDate = new Date(dateMatch[0]);
        if (!isNaN(departureDate.getTime())) {
          return departureDate;
        }
      }
    }

    return null;
  }

  reorderProducts(container, sortedElements) {
    // Clear the container
    container.innerHTML = '';

    // Append sorted elements
    sortedElements.forEach(element => {
      container.appendChild(element);
    });

    // Show the collection with a smooth fade-in after a brief delay
    setTimeout(() => {
      const collectionElement = document.querySelector(
        '.product-collection, .collection'
      );
      if (collectionElement) {
        collectionElement.classList.add('completed');
      }
    }, 50);

    // Fallback: ensure collection is visible after a longer delay in case of issues
    setTimeout(() => {
      const collectionElement = document.querySelector(
        '.product-collection, .collection'
      );
      if (
        collectionElement &&
        collectionElement.classList.contains('departure-sorting')
      ) {
        collectionElement.classList.add('completed');
      }
    }, 1000);
  }

  updateProductCount(currentCount, totalCount) {
    // Update product count displays
    const countElements = document.querySelectorAll(
      '.ProductCountDesktop, .product-count__text, [data-product-count]'
    );

    countElements.forEach(element => {
      if (element.textContent.includes('products')) {
        element.textContent = element.textContent.replace(
          /\d+/g,
          currentCount.toString()
        );
      }
    });

    // Update progress bar if it exists
    const progressBar = document.querySelector('.active-bar');
    if (progressBar && totalCount > 0) {
      const percentage = (currentCount / totalCount) * 100;
      progressBar.style.width = `${percentage}%`;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new DepartureDateHandler();
});

// Also initialize after AJAX content loads (for pagination/filtering)
document.addEventListener('shopify:section:load', () => {
  new DepartureDateHandler();
});

// For compatibility with existing AJAX loading
if (typeof InitCustomFunctions === 'function') {
  const originalInitCustomFunctions = InitCustomFunctions;
  InitCustomFunctions = function () {
    originalInitCustomFunctions();
    new DepartureDateHandler();
  };
}
